import { Instagram, Mail, Phone } from "lucide-react";
import { useLang } from "../../i18n";
import { Reveal, Overline } from "./Reveal";
import { CONTACTS, INSTAGRAM_URL } from "../../data";

export const Contact = () => {
  const { t } = useLang();

  const items = [
    {
      icon: Mail,
      label: t.contact.email,
      value: CONTACTS.email,
      href: `mailto:${CONTACTS.email}`,
      testid: "contact-email",
    },
    {
      icon: Phone,
      label: t.contact.phone,
      value: CONTACTS.phones.join("  ·  "),
      href: `tel:${CONTACTS.phones[0].replace(/[^+\d]/g, "")}`,
      testid: "contact-phone",
    },
    {
      icon: Instagram,
      label: t.contact.instagram,
      value: `@${CONTACTS.instagram}`,
      href: INSTAGRAM_URL,
      testid: "contact-instagram",
    },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="bg-ink py-24 text-cream md:py-36">
      <div className="mx-auto max-w-[1440px] px-6 md:px-14">
        <Reveal>
          <Overline dark>{t.contact.overline}</Overline>
          <h2 className="mt-5 font-serif text-5xl font-medium italic leading-none tracking-tight md:text-7xl">
            {t.contact.title}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-px bg-cream/10 md:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={item.testid} delay={i * 0.08} className="bg-ink">
              <a
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={item.testid}
                className="group flex h-full flex-col gap-6 border border-transparent p-8 transition-colors duration-500 hover:border-gold/50 md:p-10"
              >
                <item.icon size={22} className="text-gold" />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-cream/45">{item.label}</p>
                  <p className="mt-2 font-serif text-xl italic leading-snug text-cream transition-colors duration-300 group-hover:text-gold md:text-2xl">
                    {item.value}
                  </p>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="footer" className="border-t border-cream/10 bg-ink py-12 text-cream">
      <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-8 px-6 md:flex-row md:items-center md:px-14">
        <div className="flex items-center gap-4">
          <img src="/images/logo-mark.png" alt="" className="h-14 w-auto rounded-sm bg-cream p-1" />
          <div>
            <p className="font-serif text-lg font-semibold uppercase tracking-[0.14em]">New York Panettone Festival</p>
            <p className="mt-1 text-xs italic text-cream/50">{t.footer.tagline}</p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 md:items-end">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram"
            className="inline-flex items-center gap-2 text-sm text-cream/70 transition-colors duration-300 hover:text-gold"
          >
            <Instagram size={16} /> @{CONTACTS.instagram}
          </a>
          <div className="text-left md:text-right">
            <p className="text-sm text-gold" data-testid="footer-edition">{t.footer.edition}</p>
            <p className="mt-1 text-xs text-cream/40">
              © 2026 New York Panettone Festival. {t.footer.rights}{" "}
              <a href="/admin" data-testid="footer-admin-link" className="underline-offset-2 transition-colors duration-300 hover:text-gold">
                Admin
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
